import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useRouter } from 'next/router';

import { UserContext } from '~/components/providers/UserProvider';
import { ContestMutationKeys, ContestQueryKeys } from '~/domains/contest/constants/query';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type useContestEntriesWithPicks from '~/domains/contest/hooks/useTeamContestEntriesWithPicks';
import type { useLeaderboardWithPicksInfiniteScroll } from '~/domains/contest/hooks/useLeaderboardWithPicksInfinite';
import type { useLeaderboardInfiniteScroll } from '~/domains/contest/hooks/useLeaderboardInfinite';

import { updateEntry } from '../entries.service.api';
import type { UserEntry } from '../interfaces/user-entry.interface';
import type { LeaderboardEntry } from '../interfaces/leaderboard.interface';

const updateEntryEntry = (entryId: string, newName: string, entry: UserEntry) => {
  if (entry.id !== entryId) {
    return entry;
  }

  return {
    ...entry,
    name: newName,
  };
};

const updateLeaderboardEntry = (entryId: string, newName: string, entry: LeaderboardEntry) => {
  if (entry.entryId !== entryId) {
    return entry;
  }

  return {
    ...entry,
    entryName: newName,
  };
};

function useEntryMutation(entryId: string) {
  const queryClient = useQueryClient();
  const { user } = useContext(UserContext);
  const { contestUuid } = useRouter().query;

  return useMutation({
    mutationKey: ContestMutationKeys.UPDATE_ENTRY(entryId),
    mutationFn: (newName: string) => updateEntry(entryId, { name: newName }),
    onSuccess: (_, newName) => {
      queryClient.setQueriesData<PaginatedData<UserEntry>>(
        ContestQueryKeys.ENTRIES_FOR_CURRENT_USER({ contestId: contestUuid as string, userId: user?.id }),
        (prevData) => {
          if (!prevData) {
            return prevData;
          }

          return {
            ...prevData,
            data: prevData.data.map((entry) => updateEntryEntry(entryId, newName, entry)),
          };
        }
      );
      queryClient.setQueriesData<ReturnType<typeof useContestEntriesWithPicks>['data']>(
        ContestQueryKeys.ENTRIES_CURRENT_USER_WITH_PICKS({
          contestId: contestUuid,
          userId: user.id,
        }),
        (prevData) => {
          if (!prevData) {
            return prevData;
          }

          return {
            ...prevData,
            pages: prevData.pages.map((page) => ({
              ...page,
              entries: page.entries.map((entry) => updateEntryEntry(entryId, newName, entry)),
            })),
          };
        }
      );
      queryClient.setQueriesData<ReturnType<typeof useLeaderboardWithPicksInfiniteScroll>['data']>(
        ContestQueryKeys.LEADERBOARD_WITH_PICKS_INFINITE({
          contestId: contestUuid as string,
        }),
        (prevData) => {
          if (!prevData) {
            return prevData;
          }

          return {
            ...prevData,
            pages: prevData.pages.map((page) => ({
              ...page,
              leaderboardEntries: page.leaderboardEntries.map((entry) =>
                updateLeaderboardEntry(entryId, newName, entry)
              ),
            })),
          };
        }
      );
      queryClient.setQueriesData<ReturnType<typeof useLeaderboardInfiniteScroll>['data']>(
        ContestQueryKeys.LEADERBOARD_INFINITE({
          contestId: contestUuid as string,
        }).filter(Boolean),
        (prevData) => {
          if (!prevData) {
            return prevData;
          }

          return {
            ...prevData,
            pages: prevData.pages.map((page) => ({
              ...page,
              data: page.data.map((entry) => updateLeaderboardEntry(entryId, newName, entry)),
            })),
          };
        }
      );
    },
  });
}

export default useEntryMutation;
